import type { ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function OperatorPage({
  eyebrow,
  title,
  description,
  actions,
  hero,
  children,
}: {
  eyebrow: string
  title: string
  description: ReactNode
  actions?: ReactNode
  hero?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 sm:space-y-7">
      <section className="page-hero-panel space-y-5 rounded-[28px] px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">{eyebrow}</p>
            <div className="min-w-0 space-y-2">
              <h1 className="min-w-0 break-words text-[1.7rem] font-semibold tracking-[-0.02em] text-foreground sm:text-[2rem]">
                {title}
              </h1>
              <p className="max-w-3xl break-words text-sm leading-6 text-muted-foreground">{description}</p>
            </div>
          </div>
          {actions ? (
            <div className="flex min-w-0 flex-wrap items-stretch gap-3 sm:items-center xl:max-w-[45%] xl:justify-end [&>*]:min-w-0 [&>*]:max-w-full">
              {actions}
            </div>
          ) : null}
        </div>
      </section>
      {hero}
      {children}
    </div>
  )
}

export function HeroPanel({
  title,
  description,
  badge,
  metrics,
  aside,
  className,
}: {
  title: ReactNode
  description: ReactNode
  badge?: ReactNode
  metrics?: ReactNode
  aside?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('app-surface page-section-panel rounded-[28px] px-5 py-5 sm:px-6 sm:py-6', className)}>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(20rem,0.95fr)]">
        <div className="min-w-0 space-y-4">
          {badge ? <div className="flex flex-wrap items-center gap-2">{badge}</div> : null}
          <div className="space-y-2">
            <h2 className="break-words text-[1.9rem] font-semibold tracking-[-0.04em] text-foreground sm:text-[2.35rem]">
              {title}
            </h2>
            <p className="max-w-3xl break-words text-sm leading-6 text-muted-foreground">
              {description}
            </p>
          </div>
          {metrics ? <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{metrics}</div> : null}
        </div>
        {aside ? <div className="min-w-0">{aside}</div> : null}
      </div>
    </section>
  )
}

export function HeroMetric({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: ReactNode
  value: ReactNode
  detail?: ReactNode
  tone?: 'default' | 'warning' | 'success' | 'danger'
}) {
  return (
    <div
      className={cn(
        'rounded-[22px] border px-4 py-4',
        tone === 'default' && 'border-border/60 bg-background/80',
        tone === 'warning' && 'border-warning/20 bg-warning/10',
        tone === 'success' && 'border-success/20 bg-success/10',
        tone === 'danger' && 'border-error/20 bg-error/10',
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-foreground">{value}</p>
      {detail ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{detail}</p> : null}
    </div>
  )
}

export function SectionPanel({
  eyebrow,
  title,
  description,
  action,
  children,
  className,
  contentClassName,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  return (
    <Card className={cn('page-section-panel rounded-[26px]', className)}>
      <CardHeader className="gap-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 space-y-2">
            {eyebrow ? (
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {eyebrow}
              </p>
            ) : null}
            <div className="space-y-1.5">
              <CardTitle className="break-words text-xl">{title}</CardTitle>
              {description ? <CardDescription className="max-w-3xl break-words">{description}</CardDescription> : null}
            </div>
          </div>
          {action ? <div className="flex min-w-0 flex-wrap items-center gap-3">{action}</div> : null}
        </div>
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  )
}

export function InlineAction({
  label,
  onClick,
}: {
  label: ReactNode
  onClick: () => void
}) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick}>
      {label}
      <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  )
}

export function OperatorHeader({
  breadcrumb,
  title,
  description,
  actions,
  className,
}: {
  breadcrumb?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('border-b border-border/15 pb-6', className)}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 space-y-2">
          {breadcrumb ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground">
              {breadcrumb}
            </div>
          ) : null}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-foreground sm:text-3xl">{title}</h1>
            {description ? (
              <p className="max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
            {actions}
          </div>
        ) : null}
      </div>
    </section>
  )
}

export function OperatorMetricGrid({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <section className={cn('grid grid-cols-1 gap-4 md:grid-cols-4', className)}>{children}</section>
}

export function HighlightMetricCard({
  eyebrow,
  value,
  description,
  trend,
  footer,
  className,
}: {
  eyebrow: ReactNode
  value: ReactNode
  description?: ReactNode
  trend?: ReactNode
  footer?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('page-section-panel rounded-[24px] px-5 py-5 shadow-sm md:col-span-2 sm:px-6', className)}>
      <div className="flex items-start justify-between gap-4">
        <span className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">{eyebrow}</span>
        {trend ? <div className="shrink-0">{trend}</div> : null}
      </div>
      <div className="mt-4 flex items-baseline gap-3">
        <span className="text-4xl font-extrabold tracking-[-0.05em] text-foreground sm:text-5xl">{value}</span>
      </div>
      {description ? <p className="mt-3 text-sm leading-6 text-muted-foreground">{description}</p> : null}
      {footer ? <div className="mt-5">{footer}</div> : null}
    </div>
  )
}

export function MetricCard({
  eyebrow,
  value,
  description,
  accent,
  footer,
  className,
}: {
  eyebrow: ReactNode
  value: ReactNode
  description?: ReactNode
  accent?: ReactNode
  footer?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('page-section-panel rounded-[24px] px-5 py-5 shadow-sm', className)}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">{eyebrow}</span>
        {accent ? <div className="shrink-0">{accent}</div> : null}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold tracking-[-0.04em] text-foreground">{value}</p>
        {description ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p> : null}
      </div>
      {footer ? <div className="mt-4">{footer}</div> : null}
    </div>
  )
}
